import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import AiThresholdsPanel from '../components/AiThresholdsPanel';
import AiModelDashboard from '../components/AiModelDashboard';
import { useAuth } from '../context/AuthContext';
import { resolvePhotoUrl } from '../utils/photoUrl';
import { can, rolesWith } from '../utils/permissions';
import PhotoThumb from '../components/PhotoThumb';

/**
 * Expert review of the identifications the AI was unsure of -- the active-learning routing.
 *
 * Every identification is assessed as it is made (backend/ai/uncertainty.py):
 * top-1 below 0.50, a distribution spread over at least three effective
 * candidates, or the models naming different genera inside the top three. Any
 * one routes it here (GET /ai/review/queue, routes/review_queue.py), including
 * identifications made on the Identify page, which have no photo record.
 *
 * Each card starts BLIND: the photo, not the model's candidates. An expert
 * who names the taxon there answers with saw_ai_first = false and no
 * agreement; the server scores the name against the stored top-1
 * (routes/ai.py blind_agreement), and only such rows feed the live accuracy
 * on the AI dashboard. "Show AI candidates" reveals them, and an answer given
 * after that is sent with saw_ai_first = true, which training and the
 * dashboard exclude. An answered identification leaves the queue.
 *
 * Experts only: collection managers, internal experts and lab managers.
 */
// Who reads the queue: ai:read (routes/review_queue.py, 2026-10-25).
export const EXPERT_ROLES = rolesWith('ai:read');
export const REASONS = ['low_confidence', 'high_entropy', 'model_conflict'];
export const AGREEMENTS = ['agree', 'partial', 'disagree', 'unsure'];
export const PAGE_SIZE = 20;
const NEEDS_TAXON = ['partial', 'disagree'];

function ReviewCard({ item, t, onAnswered, mayAnswer = true }) {
  const [agreement, setAgreement] = useState('');
  const [taxon, setTaxon] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // A reader who gives no answer (ai:read without ai:review: the read-only
  // executive) sees the candidates at once, and no form.
  const [revealed, setRevealed] = useState(!mayAnswer);
  const [blindTaxon, setBlindTaxon] = useState('');
  const canSendBlind = blindTaxon.trim().length > 0 && !busy;
  const needsTaxon = NEEDS_TAXON.includes(agreement);
  const canSend = Boolean(agreement) && (!needsTaxon || taxon.trim().length > 0) && !busy;

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      await api.post(`/ai/identifications/${item.ai_id}/feedback`, {
        agreement,
        expert_taxon_name: taxon.trim() || null,
        notes: notes.trim() || null,
        saw_ai_first: true,
      });
      onAnswered(item.ai_id);
    } catch {
      setError(t('sendFailed'));
      setBusy(false);
    }
  };

  const sendBlind = async () => {
    setBusy(true);
    setError('');
    try {
      await api.post(`/ai/identifications/${item.ai_id}/feedback`, {
        expert_taxon_name: blindTaxon.trim(),
        notes: notes.trim() || null,
        saw_ai_first: false,
      });
      onAnswered(item.ai_id);
    } catch {
      setError(t('sendFailed'));
      setBusy(false);
    }
  };

  return (
    <article data-testid="review-item" style={cardStyle}>
      {item.image_url
        ? (
          <PhotoThumb photos={[{ s3_url: item.image_url }]} dialogLabel={t('photoAlt')} testId="review-photo-open">
            <img src={resolvePhotoUrl(item.image_url)} alt={t('photoAlt')} style={imageStyle} />
          </PhotoThumb>
        )
        : <div style={{ ...imageStyle, background: 'var(--c-bg-muted)' }} />}
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={chipRow}>
          {(item.reasons || []).map(reason => (
            <span key={reason} data-testid="review-reason" style={chipStyle}>{t(`reasons.${reason}`)}</span>
          ))}
          {item.toxicity_flag && <span style={{ ...chipStyle, background: 'var(--c-danger-soft)', color: 'var(--c-danger-3-fg)' }}>{t('toxic')}</span>}
        </div>
        {!revealed ? (
          <div data-testid="review-blind">
            <p style={mutedStyle}>{t('blind.intro')}</p>
            <input value={blindTaxon} onChange={e => setBlindTaxon(e.target.value)} aria-label={t('blind.taxon')}
                   title={t('tip.blindTaxon')} placeholder={t('blind.taxon')}
                   style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
            <textarea value={notes} onChange={e => setNotes(e.target.value)} aria-label={t('notes')} placeholder={t('notes')}
                      title={t('tip.notes')} rows={2}
                      style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginTop: 6 }} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={sendBlind} disabled={!canSendBlind} style={buttonStyle(canSendBlind)}
                      title={t('tip.blindSend')}>
                {busy ? t('sending') : t('blind.send')}
              </button>
              <button type="button" onClick={() => setRevealed(true)} disabled={busy} style={buttonStyle(!busy)}
                      title={t('tip.reveal')}>
                {t('blind.reveal')}
              </button>
            </div>
            {error && <p role="alert" style={errorStyle}>{error}</p>}
          </div>
        ) : (<>
        <ol style={guessStyle} data-testid="review-guesses" aria-label={t('candidates')}>
          {(item.ai_guesses || []).map((guess, i) => {
            const pct = guess.confidence != null ? Math.round(guess.confidence * 1000) / 10 : null;
            return (
              <li key={`${guess.taxon_name}-${i}`} style={{ marginBottom: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <em>{guess.taxon_name}</em>
                  {pct != null && <span style={mutedStyle} data-testid="review-pct">{pct}%</span>}
                </div>
                {pct != null && (
                  <div style={barTrack} aria-hidden="true">
                    <div style={{ ...barFill, width: `${Math.max(1, pct)}%`, opacity: i === 0 ? 1 : 0.6 }} />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
        <p style={mutedStyle} data-testid="review-model">
          {[
            item.model_version ? t('model', { version: item.model_version }) : null,
            item.uncertainty?.effective_candidates != null
              ? t('effectiveCandidates', { n: item.uncertainty.effective_candidates }) : null,
            item.unmapped_mass != null ? t('unmapped', { pct: Math.round(item.unmapped_mass * 1000) / 10 }) : null,
            item.inference_ms != null ? t('inferenceMs', { ms: item.inference_ms }) : null,
          ].filter(Boolean).join(' · ')}
        </p>
        <p style={mutedStyle}>
          {[item.record_code, item.created_at ? item.created_at.slice(0, 10) : null].filter(Boolean).join(' · ')}
        </p>
        {mayAnswer && (<>
        <div style={formRow}>
          <select value={agreement} onChange={e => setAgreement(e.target.value)} aria-label={t('agreement')} style={inputStyle}
                  title={t('tip.agreement')}>
            <option value="">{t('choose')}</option>
            {AGREEMENTS.map(a => <option key={a} value={a}>{t(`agreements.${a}`)}</option>)}
          </select>
          <input value={taxon} onChange={e => setTaxon(e.target.value)} aria-label={t('expertTaxon')} title={t('tip.taxon')}
                 placeholder={needsTaxon ? t('expertTaxonRequired') : t('expertTaxon')} style={inputStyle} />
        </div>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} aria-label={t('notes')} placeholder={t('notes')} title={t('tip.notes')}
                  rows={2} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginTop: 6 }} />
        <button type="button" onClick={send} disabled={!canSend} style={buttonStyle(canSend)} title={t('tip.send')}>
          {busy ? t('sending') : t('send')}
        </button>
        {error && <p role="alert" style={errorStyle}>{error}</p>}
        </>)}
        </>)}
      </div>
    </article>
  );
}

export default function ReviewQueue() {
  const { t } = useTranslation('review');
  const tRef = useRef(t);
  tRef.current = t;
  const { user } = useAuth();
  const allowed = (user?.roles || []).some(r => EXPERT_ROLES.includes(r));

  const [reason, setReason] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // The metrics are fetched only when someone opens them.
  const [showMetrics, setShowMetrics] = useState(false);

  const load = useCallback((offset, append) => {
    setLoading(true);
    setError('');
    return api.get('/ai/review/queue', { params: { limit: PAGE_SIZE, offset, ...(reason ? { reason } : {}) } })
      .then(({ data }) => {
        setItems(prev => (append ? [...prev, ...(data.items || [])] : (data.items || [])));
        setTotal(data.total || 0);
      })
      .catch(() => {
        if (!append) setItems([]);
        setError(tRef.current('loadFailed'));
      })
      .finally(() => setLoading(false));
  }, [reason]);

  useEffect(() => {
    if (allowed) load(0, false);
  }, [allowed, load]);

  if (!allowed) {
    return (
      <div style={pageStyle}>
        <h2 style={titleStyle}>{t('title')}</h2>
        <p style={noticeStyle}>{t('notPermitted')}</p>
      </div>
    );
  }

  const answered = (aiId) => {
    setItems(prev => prev.filter(i => i.ai_id !== aiId));
    setTotal(n => Math.max(0, n - 1));
  };

  return (
    <div style={pageStyle}>
      <h2 style={titleStyle}>{t('title')}</h2>
      <p style={introStyle}>{t('intro')}</p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={mutedStyle}>
          {t('filterLabel')}{' '}
          <select value={reason} onChange={e => setReason(e.target.value)} aria-label={t('filterLabel')} style={inputStyle}
                  title={t('tip.filter')}>
            <option value="">{t('filterAll')}</option>
            {REASONS.map(r => <option key={r} value={r}>{t(`reasons.${r}`)}</option>)}
          </select>
        </label>
        <span data-testid="review-total" style={mutedStyle}>{t('waiting', { count: total })}</span>
      </div>

      {error && <p role="alert" style={errorStyle}>{error}</p>}
      {!loading && !error && items.length === 0 && (
        <div style={noticeStyle} data-testid="review-empty">
          <p style={{ margin: '0 0 6px' }}>{t('empty')}</p>
          <p style={{ margin: 0 }}>{t('emptyHow')}</p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map(item => <ReviewCard key={item.ai_id} item={item} t={t} onAnswered={answered}
          mayAnswer={can(user, 'ai:review')} />)}
      </div>

      {items.length < total && (
        <button type="button" onClick={() => load(items.length, true)} disabled={loading} style={{ ...buttonStyle(!loading), marginTop: 12 }}
                title={t('tip.loadMore')}>
          {t('loadMore')}
        </button>
      )}
      <p style={{ ...mutedStyle, marginTop: 16 }}>{t('sawAiFirstNote')}</p>
      <details style={{ marginTop: 16 }} data-testid="review-metrics" onToggle={e => setShowMetrics(e.currentTarget.open)}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }} title={t('metricsTip')}>{t('metricsSummary')}</summary>
        {showMetrics && <AiModelDashboard compact />}
      </details>
      <AiThresholdsPanel />
    </div>
  );
}

const pageStyle = { maxWidth: 900, margin: '0 auto', padding: '8px 4px' };
const titleStyle = { margin: '0 0 6px', color: 'var(--c-primary-fg)' };
const introStyle = { fontSize: 14, color: 'var(--c-text-body-fg)', margin: '0 0 14px', lineHeight: 1.5 };
const mutedStyle = { fontSize: 12, color: 'var(--c-text-muted-fg)' };
const noticeStyle = { fontSize: 13, color: 'var(--c-warning-text-fg)', background: 'var(--c-warning-soft)', borderRadius: 6, padding: '8px 12px' };
const errorStyle = { fontSize: 13, color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)', borderRadius: 6, padding: '8px 12px' };
const cardStyle = { display: 'flex', gap: 14, flexWrap: 'wrap', border: '1px solid var(--c-border)', borderRadius: 8, padding: 12, background: 'var(--c-surface)' };
const imageStyle = { width: 200, height: 150, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--c-border)', maxWidth: '100%' };
const chipRow = { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 };
const chipStyle = { fontSize: 12, background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)', borderRadius: 12, padding: '2px 8px' };
const guessStyle = { margin: '0 0 6px', paddingInlineStart: 20, fontSize: 14 };
const barTrack = { height: 6, background: 'var(--c-bg-muted)', borderRadius: 3, overflow: 'hidden' };
const barFill = { height: '100%', background: 'var(--c-primary)' };
const formRow = { display: 'flex', gap: 6, flexWrap: 'wrap' };
const inputStyle = { fontSize: 14, padding: '4px 6px', borderRadius: 4, border: '1px solid var(--c-border-strong)' };
const buttonStyle = (enabled) => ({
  marginTop: 8, background: enabled ? 'var(--c-primary)' : 'var(--c-text-faint)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6,
  padding: '6px 14px', cursor: enabled ? 'pointer' : 'default', fontSize: 14,
});
